import { useState } from "react";
import { ChevronLeft, ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";

const STEPS = [
  { id: "strategies", q: "What strategies interest you?", type: "multi", required: true, any: "Open to any strategy",
    opts: ["Buy-to-let", "BRR", "Flip", "HMO", "Rent-to-rent", "Commercial/mixed-use", "Open to any strategy"] },
  { id: "areas", q: "Where in Northern Ireland?", type: "multi", required: true,
    opts: ["Belfast", "Co. Antrim", "Co. Down", "Co. Armagh", "Co. Londonderry", "Causeway Coast", "Co. Tyrone", "Co. Fermanagh", "Anywhere in NI"] },
  { id: "budget", q: "What's your purchase budget?", type: "multi", required: true, any: "Any budget",
    opts: ["Under £75k", "£75k-£150k", "£150k-£250k", "£250k-£500k", "£500k+", "Any budget"] },
  { id: "contact", q: "Your details", type: "contact", required: true },
];

export const CONSENT_TEXT = "I agree DealDeck can contact me about property deals matching my criteria.";
export const MARKETING_TEXT = "I agree to DealDeck emailing me property deals and updates. You can unsubscribe at any time.";
export const PHONE_TEXT = "I'm happy to be phoned by one of your experienced deal sourcers to discuss my bespoke deal.";
export const PHONE_HELPER = "By ticking this, you are opting in to be contacted by phone by one of our experienced deal sourcers. You can withdraw at any time.";

const Checkbox = ({ id, checked, onChange, children }) => (
  <div className="flex items-start gap-3">
    <span className="relative w-5 h-5 mt-0.5 flex-shrink-0">
      <input id={id} type="checkbox" data-testid={id} checked={!!checked} onChange={e => onChange(e.target.checked)}
        className="peer appearance-none block w-5 h-5 rounded border border-white/20 cursor-pointer transition-colors checked:bg-emerald-600 checked:border-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" />
      <Check aria-hidden="true" className="pointer-events-none absolute inset-0 m-auto w-3 h-3 text-white hidden peer-checked:block" />
    </span>
    <label htmlFor={id} className="text-slate-300 text-sm leading-relaxed cursor-pointer">{children}</label>
  </div>
);

export default function LeadForm({ utm, onComplete, onBack }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const total = STEPS.length;
  const current = STEPS[step];
  const pct = ((step + 1) / total) * 100;

  const setField = (key, val) => setData(p => ({ ...p, [key]: val }));
  const toggleMulti = (key, val, any) => {
    setData(p => {
      const arr = p[key] || [];
      if (arr.includes(val)) return { ...p, [key]: arr.filter(v => v !== val) };
      if (any && val === any) return { ...p, [key]: [any] };
      return { ...p, [key]: [...arr.filter(v => v !== any), val] };
    });
  };

  const canNext = () => {
    if (current.type === "multi") return (data[current.id] || []).length > 0;
    if (current.type === "single") return !!data[current.id];
    if (current.id === "contact") return data.name?.trim() && data.email?.trim() && data.consent;
    return true;
  };

  const next = () => { if (step < total - 1 && canNext()) { setStep(s => s + 1); setError(""); } };
  const back = () => { if (step > 0) setStep(s => s - 1); else onBack(); };

  const submit = async () => {
    if (!data.name?.trim() || !data.email?.trim()) { setError("Full name and email are required."); return; }
    if (!data.consent) { setError("Please tick the consent box to continue."); return; }
    setSubmitting(true); setError("");
    try {
      const res = await api.post("/leads/submit", { ...data, consent_text: CONSENT_TEXT, marketing_consent_text: MARKETING_TEXT, phone_consent_text: `${PHONE_TEXT} ${PHONE_HELPER}`, utm, source: "Instagram" });
      onComplete(res.data);
    } catch (e) {
      const d = e.response?.data?.detail || e.response?.data;
      setError(d?.errors ? d.errors.join(", ") : typeof d === "string" ? d : "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const Pill = ({ label, selected, onClick }) => (
    <button onClick={onClick} data-testid={`pill-${label}`}
      className={`w-full text-left px-5 py-3.5 rounded-xl text-sm font-medium border transition-all ${
        selected ? "bg-emerald-600 text-white border-emerald-500 shadow-lg shadow-emerald-500/20"
                 : "bg-white/5 text-slate-200 border-white/10 hover:border-white/25 active:bg-white/10"
      }`}>{selected && <Check className="w-4 h-4 inline mr-2" />}{label}</button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-[#0b132b] flex flex-col" data-testid="lead-form">
      {/* Progress */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <button onClick={back} className="text-slate-400 hover:text-white p-1"><ChevronLeft className="w-5 h-5" /></button>
          <span className="text-slate-500 text-xs">{step + 1} of {total}</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1.5">
          <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-6">
        <h2 className="text-white text-xl font-bold mb-6">{current.q}</h2>

        {/* Multi-select */}
        {current.type === "multi" && (
          <div className="space-y-2.5">{current.opts.map(o =>
            <Pill key={o} label={o} selected={(data[current.id] || []).includes(o)} onClick={() => toggleMulti(current.id, o, current.any)} />
          )}</div>
        )}

        {/* Single-select */}
        {current.type === "single" && (
          <div className="space-y-2.5">{current.opts.map(o =>
            <Pill key={o} label={o} selected={data[current.id] === o} onClick={() => setField(current.id, o)} />
          )}</div>
        )}

        {/* Contact + Consent (final screen) */}
        {current.type === "contact" && (
          <div className="space-y-4">
            {[{ k: "name", l: "Full name *", p: "Your name", t: "text", r: true },
              { k: "email", l: "Email *", p: "you@email.com", t: "email", r: true },
              { k: "phone", l: "Phone (optional)", p: "07712 345678", t: "tel", r: false },
            ].map(f => (
              <div key={f.k}><label htmlFor={`lead-${f.k}`} className="text-slate-400 text-xs block mb-1">{f.l}</label>
                <input id={`lead-${f.k}`} type={f.t} required={f.r} aria-required={f.r} data-testid={`lead-${f.k}`} value={data[f.k] || ""} onChange={e => setField(f.k, e.target.value)}
                  placeholder={f.p} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-emerald-500 focus:outline-none" />
              </div>
            ))}
            <Checkbox id="lead-phone-consent" checked={data.phone_consent} onChange={v => setField("phone_consent", v)}>
              {PHONE_TEXT} <span className="text-slate-500">(optional)</span>
            </Checkbox>
            <p data-testid="lead-phone-consent-helper" className="text-slate-500 text-xs -mt-2 pl-8">{PHONE_HELPER}</p>
            {data.phone_consent && !data.phone?.trim() && (
              <p data-testid="lead-phone-consent-prompt" className="text-amber-300 text-xs -mt-2 pl-8">Add your phone number so we can call you</p>
            )}
            <div className="pt-2 space-y-3">
              <Checkbox id="lead-consent" checked={data.consent} onChange={v => setField("consent", v)}>
                {CONSENT_TEXT} <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-500 underline">Privacy Policy</a>
              </Checkbox>
              <Checkbox id="lead-marketing-consent" checked={data.marketing_consent} onChange={v => setField("marketing_consent", v)}>
                {MARKETING_TEXT} <span className="text-slate-500">(optional)</span> <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-500 underline">Privacy Policy</a>
              </Checkbox>
              <p className="text-slate-500 text-xs">We only use your details to respond to your enquiry and match you with relevant deals.</p>
            </div>
          </div>
        )}

        {error && <p data-testid="lead-error" className="text-red-400 text-sm mt-4 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">{error}</p>}
      </div>

      {/* Navigation */}
      <div className="flex-shrink-0 px-5 pb-6 pt-2">
        {current.id === "contact" ? (
          <div className="flex items-center gap-3">
            <button data-testid="lead-submit" type="submit" onClick={submit} disabled={submitting}
              className={`flex-1 py-4 rounded-xl font-bold text-base transition-all ${
                data.consent && data.name?.trim() && data.email?.trim() ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-white/5 text-slate-500"
              }`}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            {!(data.consent && data.name?.trim() && data.email?.trim()) && (
              <p data-testid="lead-submit-hint" className="text-slate-400 text-xs max-w-[9rem] leading-snug">
                {!(data.name?.trim() && data.email?.trim()) ? "Add your name and email" : "Tick consent to continue"}
              </p>
            )}
          </div>
        ) : (
          <button onClick={next} disabled={!canNext()}
            className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold text-base flex items-center justify-center gap-2 transition-all">
            {!canNext() ? "Select at least one" : <>Next <ArrowRight className="w-4 h-4" /></>}
          </button>
        )}
      </div>
    </div>
  );
}
